/**
 * Faturas residuais configuradas na marcação do pagamento parcial (AF-4097).
 *
 * O modo vem de `business_properties.RESIDUAL_INVOICE` (rota `/residual-invoice-settings`). A regra
 * do que a marcação manda mora aqui, e não nas duas telas que marcam (modal "Sinalizar pagamento" e
 * step 3 da Identificação), para as duas não divergirem:
 *
 *   elemento oculto            → o campo não vai (vale o automático do modo, no backend)
 *   o usuário mexeu na lista   → vai o que ele configurou, inclusive `[]`
 *   modo AUTO* visível         → uma fatura com o saldo do título, que acompanha o valor digitado
 *   MANUAL                     → `[]`: só gera o que o usuário configurar
 */
import { ResidualInvoiceSettings, ResidualInvoiceSetup } from './billingApi';

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * A fatura que o modo pré-configura: o saldo do título, vencendo com a fatura anterior.
 *
 * Herdar a cobrança é copiar o carimbo (`dunningConfig`) da anterior; fatura anterior ao carimbo
 * usa o `collect` dela de agora, sem automações — a mesma regra do backend para o automático.
 */
export const residualDefault = (
    settings: ResidualInvoiceSettings, previous: any, balance: number,
): ResidualInvoiceSetup | null => {
    const value = round2(balance);
    if (!(value > 0)) return null;
    const stamp = previous?.dunningConfig;
    const collect = settings.keepsCollection
        ? (stamp ? !!stamp.collect : !!previous?.collect)
        : false;
    return {
        value,
        due_date: (previous?.dueDate || '').slice(0, 10) || null,
        with_boleto: settings.withBoleto,
        collect,
        automatic_preventive_collection: collect && !!stamp?.automaticPreventiveCollection,
        automatic_overdue_collection: collect && !!stamp?.automaticOverdueCollection,
        bill_charges: null,
        notes: null,
    };
};

/**
 * O que a marcação manda em `residual_invoices`. `undefined` = o campo não vai.
 *
 * `configured` é `null` enquanto o usuário não mexeu na lista: até lá a pré-configuração é
 * DERIVADA do saldo, e por isso acompanha o valor digitado sem efeito colateral nenhum.
 */
export const effectiveResidualInvoices = (
    settings: ResidualInvoiceSettings | null,
    configured: ResidualInvoiceSetup[] | null,
    previous: any,
    balance: number,
): ResidualInvoiceSetup[] | undefined => {
    if (!settings?.visible) return undefined;
    if (configured) return configured;
    if (!settings.prefilled) return [];
    const setup = residualDefault(settings, previous, balance);
    return setup ? [setup] : [];
};

const cents = (n: number) => Math.round(n * 100);

/**
 * A soma da lista contra o saldo do título, em centavos exatos. `null` quando não há o que avisar:
 * lista vazia (o `MANUAL` nasce assim) ou soma igual ao saldo. É só aviso — o backend aceita soma
 * diferente.
 */
export const residualSumWarning = (
    setups: ResidualInvoiceSetup[], balance: number,
): { sum: number; difference: number } | null => {
    if (setups.length === 0) return null;
    const sum = setups.reduce((acc, setup) => acc + cents(Number(setup.value)), 0);
    const difference = sum - cents(balance);
    return difference === 0 ? null : { sum: sum / 100, difference: difference / 100 };
};

/** A configuração gravada na marcação (camelCase da listagem) de volta ao formato do payload. */
export const setupsFromMarking = (raw?: any[] | null): ResidualInvoiceSetup[] | null => (
    raw == null ? null : raw.map(r => ({
        value: Number(r.value),
        due_date: r.dueDate ?? null,
        with_boleto: !!r.withBoleto,
        collect: r.collect !== false,
        automatic_preventive_collection: !!r.automaticPreventiveCollection,
        automatic_overdue_collection: !!r.automaticOverdueCollection,
        bill_charges: r.billCharges ?? null,
        notes: r.notes ?? null,
    }))
);
